import React, { useEffect, useMemo, useState } from 'react';
import Select from 'react-select';
import { useAuthFetch } from '../hooks/useAuthFetch';
import { useNavigate } from 'react-router-dom';
import '../stili/AggiornaStaff.css';

const API_URL = process.env.REACT_APP_API_URL;

const emptyForm = {
  id: null,
  nome: '',
  cognome: '',
  telefono: '',
  email: '',
  numeroTesserino: '',
  stato: 'ATTIVO',
  categorieSelezionate: [],
  categoriaSelezionata: ''
};

export default function AggiornaStaff() {
  const authFetch = useAuthFetch();
  const navigate = useNavigate();

  const [categorie, setCategorie] = useState([]);
  const [staffList, setStaffList] = useState([]);
  const [staffSelezionato, setStaffSelezionato] = useState(null);
  const [schedaAttiva, setSchedaAttiva] = useState('anagrafica');

  const [formEdit, setFormEdit] = useState(emptyForm);

  const [message, setMessage] = useState('');
  const [filtroStato, setFiltroStato] = useState('ATTIVO');

  const categorieById = useMemo(() => {
    const m = new Map();
    (categorie || []).forEach((c) => m.set(String(c.id), c));
    return m;
  }, [categorie]);

  useEffect(() => {
    async function fetchCategorie() {
      try {
        const res = await authFetch(`${API_URL}/api/admin/lookup/categorie`);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        setCategorie(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error(err);
        setCategorie([]);
      }
    }

    fetchCategorie();
  }, [authFetch]);

  useEffect(() => {
    async function fetchStaff() {
      try {
        setMessage('');
        setStaffSelezionato(null);
        setFormEdit(emptyForm);

        const params = new URLSearchParams();
        params.set('page', '0');
        params.set('size', '200');
        params.set('sort', 'cognome');
        params.set('dir', 'ASC');

        if (filtroStato !== 'TUTTI') {
          params.set('stato', filtroStato);
        }

        const url = `${API_URL}/api/admin/staff?${params.toString()}`;
        const res = await authFetch(url);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);

        const data = await res.json();

        if (data && Array.isArray(data.content)) {
          setStaffList(data.content);
        } else if (Array.isArray(data)) {
          setStaffList(data);
        } else {
          setStaffList([]);
        }
      } catch (err) {
        console.error(err);
        setStaffList([]);
        setMessage('❌ Errore durante il caricamento');
      }
    }

    fetchStaff();
  }, [authFetch, filtroStato]);

  function getCategorieIds(staff) {
    return Array.isArray(staff?.idCategorie)
      ? staff.idCategorie.map((x) => String(x))
      : [];
  }

  const staffOptions = useMemo(() => {
    return [...staffList]
      .sort((a, b) =>
        `${a.cognome} ${a.nome}`.localeCompare(`${b.cognome} ${b.nome}`)
      )
      .map((staff) => ({
        value: String(staff.id),
        label: `${staff.cognome} ${staff.nome}${staff.email ? ` (${staff.email})` : ''}`,
        staff
      }));
  }, [staffList]);

  const selectedStaffOption = useMemo(() => {
    if (!staffSelezionato) return null;
    return (
      staffOptions.find((opt) => String(opt.value) === String(staffSelezionato.id)) || null
    );
  }, [staffOptions, staffSelezionato]);

  const handleSelectStaff = (staff) => {
    if (!staff) {
      setStaffSelezionato(null);
      setFormEdit(emptyForm);
      setSchedaAttiva('anagrafica');
      return;
    }

    setStaffSelezionato(staff);
    setFormEdit({
      id: staff.id,
      nome: staff.nome ?? '',
      cognome: staff.cognome ?? '',
      telefono: staff.telefono ?? '',
      email: staff.email ?? '',
      numeroTesserino: staff.numeroTesserino ?? '',
      stato: staff.stato ?? 'ATTIVO',
      categorieSelezionate: getCategorieIds(staff),
      categoriaSelezionata: ''
    });
    setSchedaAttiva('anagrafica');
    setMessage('');
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;

    setFormEdit((prev) => {
      let v = value;

      if (name === 'nome' || name === 'cognome') {
        v = value.toUpperCase();
      }

      return { ...prev, [name]: v };
    });
  };

  const aggiungiCategoria = () => {
    const sel = formEdit.categoriaSelezionata;
    if (!sel) return;

    if (!formEdit.categorieSelezionate.includes(sel)) {
      setFormEdit((prev) => ({
        ...prev,
        categorieSelezionate: [...prev.categorieSelezionate, sel],
        categoriaSelezionata: ''
      }));
    }
  };

  const rimuoviCategoria = (index) => {
    setFormEdit((prev) => ({
      ...prev,
      categorieSelezionate: prev.categorieSelezionate.filter((_, i) => i !== index)
    }));
  };

  const handleSave = async () => {
    if (!staffSelezionato) return;

    try {
      setMessage('');

      if (!formEdit.categorieSelezionate || formEdit.categorieSelezionate.length === 0) {
        setMessage('❌ Selezionare almeno una categoria');
        setSchedaAttiva('categorie');
        return;
      }

      const payload = {
        cognome: formEdit.cognome?.trim(),
        nome: formEdit.nome?.trim(),
        telefono: formEdit.telefono?.trim() ? formEdit.telefono.trim() : null,
        email: formEdit.email?.trim(),
        numeroTesserino: formEdit.numeroTesserino?.trim()
          ? formEdit.numeroTesserino.trim()
          : null,
        stato: formEdit.stato || staffSelezionato.stato || 'ATTIVO',
        idCategorie: (formEdit.categorieSelezionate || []).map((x) => Number(x))
      };

      const res = await authFetch(`${API_URL}/api/admin/staff/${staffSelezionato.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      const rawText = await res.text();
      let data = {};

      try {
        data = rawText ? JSON.parse(rawText) : {};
      } catch {
        data = { message: rawText };
      }

      if (!res.ok) {
        throw new Error(data?.message || data?.error || rawText || `HTTP ${res.status}`);
      }

      const updated = data;

      setStaffList((prev) =>
        prev.map((s) => (s.id === staffSelezionato.id ? updated : s))
      );

      handleSelectStaff(updated);
      setMessage('✅ Modifiche salvate');
      setSchedaAttiva('azioni');
    } catch (err) {
      console.error(err);
      setMessage(`❌ ${err.message}`);
    }
  };

  const toggleStato = async () => {
    if (!staffSelezionato) return;

    try {
      setMessage('');

      const isAttivo = String(staffSelezionato.stato || '').toUpperCase() === 'ATTIVO';
      const endpoint = isAttivo ? 'disattiva' : 'riattiva';

      const res = await authFetch(`${API_URL}/api/admin/staff/${staffSelezionato.id}/${endpoint}`, {
        method: 'PUT'
      });

      if (!res.ok) {
        const txt = await res.text();
        throw new Error(txt || `HTTP ${res.status}`);
      }

      const nuovoStato = isAttivo ? 'INATTIVO' : 'ATTIVO';

      setStaffList((prev) =>
        prev.map((s) =>
          s.id === staffSelezionato.id ? { ...s, stato: nuovoStato } : s
        )
      );

      setStaffSelezionato((prev) => (prev ? { ...prev, stato: nuovoStato } : prev));
      setFormEdit((prev) => ({ ...prev, stato: nuovoStato }));

      setMessage(isAttivo ? '✅ Staff disattivato' : '✅ Staff riattivato');
      setSchedaAttiva('azioni');
    } catch (err) {
      console.error(err);
      setMessage('❌ Errore durante l’operazione');
    }
  };

  return (
    <div className="aggstaff-container" translate="no">
      <h2 className="aggstaff-title">Aggiorna Staff</h2>

      <div className="aggstaff-card aggstaff-card-filtri">
        <h3 className="aggstaff-sezione-titolo">Ricerca staff</h3>

        <div className="aggstaff-filtri-grid">
          <div className="aggstaff-form-group">
            <label className="aggstaff-label">Stato</label>
            <select
              className="aggstaff-select"
              value={filtroStato}
              onChange={(e) => setFiltroStato(e.target.value)}
            >
              <option value="ATTIVO">ATTIVO</option>
              <option value="INATTIVO">INATTIVO</option>
              <option value="TUTTI">TUTTI</option>
            </select>
          </div>

          <div className="aggstaff-form-group">
            <label className="aggstaff-label">Staff</label>
            <Select
              classNamePrefix="aggstaff-react-select"
              options={staffOptions}
              value={selectedStaffOption}
              onChange={(selectedOption) =>
                handleSelectStaff(selectedOption ? selectedOption.staff : null)
              }
              placeholder="Cerca e seleziona uno staff..."
              isClearable
              isSearchable
              noOptionsMessage={() => 'Nessuno staff trovato'}
            />
          </div>
        </div>
      </div>

      {staffSelezionato && (
        <div className="aggstaff-card aggstaff-card-modifica">
          <div className="aggstaff-tabs">
            <button
              type="button"
              className={`aggstaff-tab ${schedaAttiva === 'anagrafica' ? 'active' : ''}`}
              onClick={() => setSchedaAttiva('anagrafica')}
            >
              Anagrafica
            </button>

            <button
              type="button"
              className={`aggstaff-tab ${schedaAttiva === 'categorie' ? 'active' : ''}`}
              onClick={() => setSchedaAttiva('categorie')}
            >
              Categorie
            </button>

            <button
              type="button"
              className={`aggstaff-tab ${schedaAttiva === 'azioni' ? 'active' : ''}`}
              onClick={() => setSchedaAttiva('azioni')}
            >
              Azioni
            </button>
          </div>

          <div className="aggstaff-tab-content">
            {schedaAttiva === 'anagrafica' && (
              <div className="aggstaff-sezione">
                <h3 className="aggstaff-sezione-titolo">Dati anagrafici</h3>

                <div className="aggstaff-grid-2">
                  <div className="aggstaff-form-group">
                    <label className="aggstaff-label">Nome</label>
                    <input
                      className="aggstaff-input"
                      name="nome"
                      value={formEdit.nome || ''}
                      onChange={handleEditChange}
                    />
                  </div>

                  <div className="aggstaff-form-group">
                    <label className="aggstaff-label">Cognome</label>
                    <input
                      className="aggstaff-input"
                      name="cognome"
                      value={formEdit.cognome || ''}
                      onChange={handleEditChange}
                    />
                  </div>

                  <div className="aggstaff-form-group">
                    <label className="aggstaff-label">Telefono</label>
                    <input
                      className="aggstaff-input"
                      name="telefono"
                      value={formEdit.telefono || ''}
                      onChange={handleEditChange}
                    />
                  </div>

                  <div className="aggstaff-form-group">
                    <label className="aggstaff-label">Email</label>
                    <input
                      className="aggstaff-input"
                      name="email"
                      value={formEdit.email || ''}
                      onChange={handleEditChange}
                    />
                  </div>

                  <div className="aggstaff-form-group">
                    <label className="aggstaff-label">Numero Tesserino</label>
                    <input
                      className="aggstaff-input"
                      name="numeroTesserino"
                      value={formEdit.numeroTesserino || ''}
                      onChange={handleEditChange}
                    />
                  </div>

                  <div className="aggstaff-form-group">
                    <label className="aggstaff-label">Stato</label>
                    <input
                      className="aggstaff-input"
                      value={formEdit.stato || ''}
                      disabled
                    />
                  </div>
                </div>
              </div>
            )}

            {schedaAttiva === 'categorie' && (
              <div className="aggstaff-sezione">
                <h3 className="aggstaff-sezione-titolo">Categorie di competenza</h3>

                <div className="aggstaff-form-group">
                  <label className="aggstaff-label">Categorie</label>

                  <div className="aggstaff-categoria-add-row">
                    <select
                      className="aggstaff-select"
                      name="categoriaSelezionata"
                      value={formEdit.categoriaSelezionata || ''}
                      onChange={handleEditChange}
                    >
                      <option value="">-- Seleziona categoria --</option>
                      {categorie.map((c) => (
                        <option key={c.id} value={String(c.id)}>
                          {c.nome}
                        </option>
                      ))}
                    </select>

                    <button
                      type="button"
                      className="aggstaff-mini-button"
                      onClick={aggiungiCategoria}
                    >
                      +
                    </button>
                  </div>

                  <div className="aggstaff-categorie-list">
                    {(formEdit.categorieSelezionate || []).length === 0 ? (
                      <div className="aggstaff-empty-state">
                        Nessuna categoria selezionata
                      </div>
                    ) : (
                      (formEdit.categorieSelezionate || []).map((catId, i) => {
                        const cat = categorieById.get(String(catId));
                        return (
                          <div key={`${catId}-${i}`} className="aggstaff-categoria-item">
                            <span>{cat ? cat.nome : catId}</span>
                            <button
                              type="button"
                              className="aggstaff-mini-button"
                              onClick={() => rimuoviCategoria(i)}
                            >
                              -
                            </button>
                          </div>
                        );
                      })
                    )}
                  </div>
                </div>
              </div>
            )}

            {schedaAttiva === 'azioni' && (
              <div className="aggstaff-sezione">
                <h3 className="aggstaff-sezione-titolo">Azioni</h3>

                <div className="aggstaff-azioni-box">
                  <div className="aggstaff-stato-box">
                    <span className="aggstaff-stato-label">Stato attuale:</span>
                    <span className="aggstaff-stato-value">
                      {String(staffSelezionato.stato || '').toUpperCase()}
                    </span>
                  </div>

                  <div className="aggstaff-bottoni-espansi">
                    <button
                      type="button"
                      className="aggstaff-salva-btn"
                      onClick={handleSave}
                    >
                      Salva
                    </button>

                    <button
                      type="button"
                      className="aggstaff-annulla-btn"
                      onClick={() => handleSelectStaff(null)}
                    >
                      Annulla
                    </button>

                    <button
                      type="button"
                      className="aggstaff-toggle-btn"
                      onClick={toggleStato}
                    >
                      {String(staffSelezionato.stato || '').toUpperCase() === 'ATTIVO'
                        ? 'Disattiva'
                        : 'Riattiva'}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {message && (
        <div
          className={`staff-message ${
            message.startsWith('✅') ? 'staff-success' : 'staff-error'
          }`}
        >
          {message}
        </div>
      )}

      <div className="staff-button-row">
        <button
          className="staff-custom-button"
          onClick={() => navigate('/gestionale')}
        >
          Torna al Gestionale
        </button>
      </div>
    </div>
  );
}